import { useParams } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import {
  Spin,
  Tabs,
  Form,
  Select,
  message,
  Button,
  Popconfirm,
  Tooltip,
  Dropdown,
  Breadcrumb,
  Tag,
  Divider,
  Drawer,
} from 'antd';
import PageMeta from 'components/PageMeta/PageMeta';
import PageTitleBar from 'components/PageTitleBar/PageTitleBar';
import ApplicationDetails from './ApplicationDetails';
import { CareLocationDetails } from './CareLocationDetails';
import { DocumentDetails } from './DocumentDetails';
import { FacilityCredentials } from './FacilityCredentials';
import { Agreement } from './Agreement';
import { getFullName, hasPermission } from 'utils';
import RadioSelect from 'components/Select/RadioSelect';
import { useAsync } from 'react-async-hook';
import { commonService, legacyService, providerService } from 'services';
import { ROLES } from 'constants';
import { selectRoles, selectUser } from 'features/auth';
import { startCase } from 'lodash-es';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom/cjs/react-router-dom';
import Space from 'components/Space/Space';
import { Building, Chemistry, ChemistryReference, ChevronDown } from '@carbon/icons-react';
import { ENTITY_TYPES } from 'constants';
import ProviderApplicationNotesCard from './ApplicationDetails/ProviderApplicationNotesCard';
import { PROVIDER_STATUSES } from 'constants';
import { useFlags } from 'launchdarkly-react-client-sdk';
import './ProviderApplication.module.less';
import Card from 'components/Card/Card';
import { useUnsavedChangesGuard } from 'components/Card/useEditCard';
import AddressContactInfo from './AddressContactInfo';
import ProgramTypesSelector from './ProgramTypesSelector';
import { EMDASH } from 'constants';
import ApplicationStatusCard from './ApplicationStatusCard';
import ProviderImportStatusBanner from './ProviderImportStatusBanner';
import ReviewActions from './ReviewActions';

const ProviderApplication = () => {
  const { id } = useParams();
  const history = useHistory();
  const location = useLocation();
  const searchParams = queryString.parse(location.search);
  const { providerLegacyImportSync, providerServicesApplicationEditing } = useFlags();
  useUnsavedChangesGuard();

  const currentUser = useSelector(selectUser);
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState(null);
  const [form] = Form.useForm();
  const [selectedStatus, setSelectedStatus] = useState(null);
  const [selectedAssignee, setSelectedAssignee] = useState(null);
  const [assigneePlaceholder, setAssigneePlaceholder] = useState('');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [importStatusTrail, setImportStatusTrail] = useState([]);
  const [importStatusLoading, setImportStatusLoading] = useState(false);
  const [importStatusSyncing, setImportStatusSyncing] = useState(false);

  const fetchImportStatus = useCallback(async () => {
    try {
      setImportStatusLoading(true);
      const statuses = await legacyService.getProviderImportStatus(id);
      setImportStatusTrail(statuses || []);
    } catch {
      // import status is non-critical — silently ignore fetch errors
    } finally {
      setImportStatusLoading(false);
    }
  }, [id]);

  const { result: statusOptions = [] } = useAsync(async () => {
    return commonService.get('/providers/application-status-types/internal', { cache: { interpretHeader: false } });
  }, []);

  const { result: assigneeOptions = [] } = useAsync(async () => {
    return commonService.get(`/users/all?role=${ROLES.PROVIDER_COORDINATOR}`);
  }, []);

  const { result: careFacility } = useAsync(async () => {
    if (application?.careFacility?.id) {
      return providerService.getCenterById(Number(application.careFacility.id));
    }
    return null;
  }, [application?.careFacility?.id]);

  const { result: careFacilityApplications = null } = useAsync(async () => {
    if (application?.careFacility?.id) {
      return providerService.getCarefacilityApplications(application.careFacility.id);
    }

    return null;
  }, [application?.careFacility?.id]);

  const fetchApplication = useCallback(() => {
    setLoading(true);
    providerService
      .getApplication(id)
      .then((data) => {
        setApplication(data);
      })
      .catch((error) => {
        console.error('Error fetching application', error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [id]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication, id]);

  useEffect(() => {
    if (application && statusOptions?.length && assigneeOptions?.length) {
      setSelectedStatus(application?.status?.status?.id);
      setSelectedAssignee(application?.assignedTo?.id);
      setAssigneePlaceholder('Unassigned');

      if (providerLegacyImportSync && application?.status?.status?.title === PROVIDER_STATUSES.REVIEWED) {
        fetchImportStatus();
      } else {
        setImportStatusTrail([]);
      }
    }
  }, [application, statusOptions, assigneeOptions, fetchImportStatus, providerLegacyImportSync]);

  useEffect(() => {
    if (!searchParams.activeTab || (!providerLegacyImportSync && searchParams.activeTab === 'review-actions')) {
      searchParams.activeTab = 'application-details';
      history.replace(`${location.pathname}?${queryString.stringify(searchParams)}`);
    }
  }, [history, location.pathname, providerLegacyImportSync, searchParams]);

  const handleSave = async (assignee) => {
    try {
      setLoading(true);
      setSelectedAssignee(assignee);
      await providerService.assignCoordinator([
        {
          userId: assignee,
          applicationId: id,
        },
      ]);
    } catch (error) {
      console.error('Error assigning application', error);
      message.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    try {
      setLoading(true);
      setSelectedAssignee(null);
      await providerService.unAssignCoordinator([{ applicationId: id }]);
    } catch (error) {
      console.error('Error unassigning application', error);
      message.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const statusChanged = async (newStatus) => {
    try {
      setLoading(true);
      setSelectedStatus(newStatus);

      const _newApplication = await providerService.updateApplicationStatus(id, newStatus);
      setApplication(_newApplication);

      if (providerLegacyImportSync && _newApplication?.status?.status?.title === PROVIDER_STATUSES.REVIEWED) {
        setImportStatusTrail([{ importStatus: 'IN_PROGRESS' }]);
        setTimeout(fetchImportStatus, 3000);
      }
    } catch (error) {
      console.error('Error updating application status', error);
      message.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const syncLegacyImport = async () => {
    try {
      setImportStatusSyncing(true);
      setImportStatusTrail([{ importStatus: 'IN_PROGRESS' }]);
      await legacyService.importProviderApplication(id);
      message.success('Legacy sync started');
      setTimeout(fetchImportStatus, 3000);
    } catch (error) {
      console.error('Error syncing application to Legacy', error);
      message.error('Something went wrong');
      fetchImportStatus();
    } finally {
      setImportStatusSyncing(false);
    }
  };

  const _isTestAccount = application?.careFacility?.organization?.testRecord === true;
  // Only taken into account when the application __IS__ a test account
  const [popConfirmIsOpen, setPopConfirmIsOpen] = useState(false);

  const userRoles = useSelector(selectRoles);
  const _userHasPermissionToToggleAsTest = hasPermission(userRoles, [ROLES.PROVIDER_MANAGER]);
  const canEditPCSContent = hasPermission(userRoles, [ROLES.PROVIDER_MANAGER, ROLES.PROVIDER_COORDINATOR]);

  const handleProgramSwitch = (applicationId) => {
    setDrawerOpen(false);
    history.push(
      `/providers/applications/${applicationId}?activeTab=${searchParams.activeTab || 'application-details'}`,
    );
  };

  return (
    <Spin size="large" spinning={loading}>
      <div className="min-h-screen">
        <PageMeta title="Application Details" />

        <Breadcrumb
          className="mb-6 pt-6 px-6 md:px-0 md:pt-0 md:pb-4"
          items={[
            {
              title: 'Provider Queue',
              href: '/providers',
              onClick: (e) => {
                e.preventDefault();
                history.push('/providers');
              },
            },
            {
              title:
                application?.careFacility?.businessLegalName || application?.careFacility?.doingBusinessAs || EMDASH,
              href: `/providers/orgs/${application?.careFacility?.orgId}`,
              onClick: (e) => {
                e.preventDefault();
                history.push(`/providers/orgs/${application?.careFacility?.orgId}`);
              },
            },
            {
              title: `APP-${application?.id || EMDASH}`,
            },
          ]}
        />

        <PageTitleBar
          title={application?.careFacility?.businessLegalName}
          subtitle={
            <div className="space-y-4">
              {application?.careFacility?.doingBusinessAs &&
                application?.careFacility?.doingBusinessAs !== application?.careFacility?.businessLegalName && (
                  <div>{application?.careFacility?.doingBusinessAs}</div>
                )}

              <div className="flex items-center gap-3 flex-wrap">
                P#{application?.careFacility?.id ?? ''}
                {application?.careFacility?.careFacilityType?.title && (
                  <Tag className="!bg-white/15 !border-white/25 !text-white/85 font-medium text-[11px] !py-1/2 !px-3 flex items-center gap-1.5 !rounded-full">
                    {application.careFacility.careFacilityType.title}
                  </Tag>
                )}
              </div>
            </div>
          }
          extra={
            <div className="flex items-center gap-3">
              <Button
                onClick={() => setDrawerOpen(true)}
                icon={<Building />}
                variant="filled"
                color="default"
                className="border-solid border-white border-opacity-5 !bg-white !bg-opacity-5 !text-white hover:!bg-white hover:!bg-opacity-10"
              >
                Provider Details
              </Button>

              {application?.careFacility?.organization && (
                <Popconfirm
                  placement="bottomRight"
                  description={
                    <div className="w-[70vw] max-w-[250px]">
                      {!_isTestAccount ? (
                        <>
                          You are about to change this to a test account. This will remove it from processing and
                          reporting.
                          <p className="block mt-4">Are you sure you'd like to proceed?</p>
                        </>
                      ) : (
                        <>
                          You are about to convert this record to a real account. This will include it in processing and
                          reporting.
                          <p className="block mt-4">Are you sure you'd like to proceed?</p>
                        </>
                      )}
                    </div>
                  }
                  onConfirm={() => {
                    if (!_userHasPermissionToToggleAsTest) {
                      return;
                    }

                    const _newTestRecord = !_isTestAccount;

                    providerService
                      .updateOrganization(application?.careFacility?.organization?.id, {
                        testRecord: _newTestRecord,
                      })
                      .then(() => {
                        message.success('Done');

                        // Refresh the application
                        fetchApplication();
                      })
                      .catch((error) => {
                        console.error('Error updating organization', error);
                        message.error('Something went wrong');
                      });
                  }}
                  onCancel={null}
                  okText={!_isTestAccount ? 'Mark as Test' : 'Mark as Real Account'}
                  okButtonProps={{ danger: true }}
                  cancelText="Cancel"
                  open={_isTestAccount ? popConfirmIsOpen : undefined}
                  onOpenChange={(open) => {
                    if (_isTestAccount && open === false) {
                      setPopConfirmIsOpen(open);
                    }
                  }}
                >
                  <Tooltip
                    title={!_userHasPermissionToToggleAsTest ? 'Only Provider Managers can toggle the test status' : ''}
                  >
                    {!_isTestAccount ? (
                      <Button
                        disabled={!_userHasPermissionToToggleAsTest}
                        className={!_userHasPermissionToToggleAsTest ? '!text-gray-500 !border-gray-500' : ''}
                        icon={<ChemistryReference />}
                        ghost
                      >
                        Mark as Test Account
                      </Button>
                    ) : _userHasPermissionToToggleAsTest ? (
                      <Dropdown
                        menu={{
                          items: [
                            {
                              label: 'Mark as real account',
                              key: 'mark-as-real',
                              onClick: () => {
                                setPopConfirmIsOpen(true);
                              },
                            },
                          ],
                        }}
                        trigger={['click']}
                      >
                        <Button ghost icon={<Chemistry className="mb-[-1px] ml-[-1px] mr-[-2px]" />}>
                          <Space size={3}>
                            Test Account
                            {_userHasPermissionToToggleAsTest && <ChevronDown className="mb-[-1px]" />}
                          </Space>
                        </Button>
                      </Dropdown>
                    ) : (
                      <Button disabled className="!text-gray-500 !border-gray-500">
                        Test Account
                      </Button>
                    )}
                  </Tooltip>
                </Popconfirm>
              )}
            </div>
          }
          noChildrenPadding
        >
          {careFacilityApplications ? (
            <div className="p-8">
              <ProgramTypesSelector applications={careFacilityApplications} currentApplicationId={id} />
            </div>
          ) : (
            <div className="px-8 py-4">
              <Spin size="small" />
            </div>
          )}
        </PageTitleBar>

        {/* Main Section */}
        <div className="flex flex-col lg:flex-row gap-4 mt-8 pb-48 md:pb-0 px-6 md:px-0">
          {/* Left Column */}
          <div className="lg:w-2/3 w-full">
            <Tabs
              activeKey={searchParams.activeTab}
              destroyOnHidden
              items={[
                {
                  key: 'care-location-details',
                  label: 'Facility information',
                  children: (
                    <CareLocationDetails
                      application={application}
                      isEditable={providerServicesApplicationEditing && canEditPCSContent}
                    />
                  ),
                },
                {
                  key: 'facility-credentials',
                  label: 'Facility credentials',
                  children: (
                    <FacilityCredentials
                      application={application}
                      isEditable={providerServicesApplicationEditing && canEditPCSContent}
                    />
                  ),
                },
                {
                  key: 'application-details',
                  label: 'Application details',
                  children: <ApplicationDetails isEditable={providerServicesApplicationEditing && canEditPCSContent} />,
                },
                {
                  key: 'agreement',
                  label: 'Agreement',
                  children: <Agreement application={application} />,
                },
                {
                  key: 'documents',
                  label: 'Documents',
                  children: <DocumentDetails application={application} isEditable={canEditPCSContent} />,
                },
                providerLegacyImportSync && {
                  key: 'review-actions',
                  label: 'Review Actions',
                  children: (
                    <ReviewActions
                      application={application}
                      importStatusTrail={importStatusTrail}
                      loadingImportStatus={importStatusLoading}
                      syncingImportStatus={importStatusSyncing}
                      onSyncImport={syncLegacyImport}
                    />
                  ),
                },
              ].filter(Boolean)}
              onChange={(nextKey) => {
                searchParams.activeTab = nextKey;
                history.push(`${location.pathname}?${queryString.stringify(searchParams)}`);
              }}
              tabBarStyle={{
                marginBottom: '20px',
              }}
            />
          </div>

          {/* Right Column */}
          <div className="lg:w-1/3 w-full">
            {application?.status?.status?.description && (
              <ApplicationStatusCard status={application.status.status.description} className="mb-6" />
            )}

            <Card title="Review application">
              <Form form={form} layout="vertical" data-testid="provider-application-review-form">
                <Tooltip
                  title={
                    !canEditPCSContent
                      ? 'Only Provider Managers and Provider Coordinators can update the status'
                      : application?.status?.status?.title === PROVIDER_STATUSES.INCOMPLETE
                      ? 'Status cannot be updated when application is incomplete'
                      : undefined
                  }
                >
                  <Form.Item label="Status" required>
                    <Select
                      name="review-status"
                      options={statusOptions
                        .sort((a, b) => {
                          const order = ['new', 'reviewed', 'incomplete'];
                          return order.indexOf(a.title.toLowerCase()) - order.indexOf(b.title.toLowerCase());
                        })
                        .map((o) => ({ value: o.id, label: startCase(o?.title?.toLowerCase()) }))}
                      value={selectedStatus}
                      onChange={statusChanged}
                      aria-label="Review Status"
                      data-testid="application-assign-status"
                      disabled={
                        !canEditPCSContent || application?.status?.status?.title === PROVIDER_STATUSES.INCOMPLETE
                      }
                    ></Select>
                  </Form.Item>
                </Tooltip>

                {providerLegacyImportSync && (
                  <ProviderImportStatusBanner importStatus={importStatusTrail?.[0]} className="mb-4" />
                )}

                <Tooltip
                  title={
                    !canEditPCSContent
                      ? 'Only Provider Managers and Provider Coordinators can update the assignee'
                      : undefined
                  }
                >
                  <Form.Item label="Assignee" required>
                    <RadioSelect
                      value={selectedAssignee}
                      onSave={handleSave}
                      onCancel={handleCancel}
                      saveText="Save"
                      cancelText="Clear"
                      placeholder={assigneePlaceholder}
                      options={[
                        { label: getFullName(currentUser), value: currentUser?.id },
                        ...(assigneeOptions || [])
                          .filter((assignee) => assignee.id !== currentUser?.id)
                          .map((assignee) => ({ label: getFullName(assignee), value: assignee.id })),
                      ]}
                      disabled={!canEditPCSContent}
                      data-testid="application-assign-reviewer"
                    />
                  </Form.Item>
                </Tooltip>
              </Form>
            </Card>

            <ProviderApplicationNotesCard
              entityType={ENTITY_TYPES.CARE_FACILITY}
              applicationId={id}
              mode="compact"
              className="mt-6"
              isEditable={canEditPCSContent}
            />
          </div>
        </div>

        {/* Provider Details Drawer */}
        <Drawer
          title="Provider Details"
          placement="right"
          onClose={() => setDrawerOpen(false)}
          open={drawerOpen}
          width={640}
          styles={{
            body: { backgroundColor: '#ffffff', color: '#000000' },
            header: { backgroundColor: '#ffffff', color: '#000000' },
          }}
        >
          <div className="space-y-6 bg-white text-black">
            <AddressContactInfo application={application} careFacility={careFacility} lightMode />

            <Divider />

            <ProgramTypesSelector
              applications={careFacilityApplications}
              currentApplicationId={id}
              onProgramSwitch={handleProgramSwitch}
              lightMode
            />
          </div>
        </Drawer>
      </div>
    </Spin>
  );
};

export default ProviderApplication;
